import { useMemo, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Clock, Zap } from "lucide-react";
import { cn } from "@/shared/lib/cn";

/**
 * ═══════════════════════════════════════════════════════════════════
 * La CARD credencial del panel (Anahí, 27-ago): el marco que comparten
 * Mi perfil y KYC — "debe ser un componente también estas cards".
 *
 * Blanca (ya no la tinta de la primera versión), centrada, con el
 * resplandor violeta y la franja de la marca abajo, la fila de estado
 * con el reloj, el encabezado con avatar de iniciales, y las PESTAÑAS
 * píldora — cada pestaña enseña su propia card de contenido.
 * ═══════════════════════════════════════════════════════════════════
 */

export interface CredentialTab {
  key: string;
  label: string;
  icon: ReactNode;
}

export function CredentialCard({
  statusLeft,
  initials,
  title,
  subtitle,
  tabs,
  activeTab,
  onTabChange,
  children,
  className,
}: {
  /** Lo de la izquierda de la fila de estado. Sin él: "Cuenta activa". */
  statusLeft?: ReactNode;
  initials: string;
  title: string;
  subtitle: string;
  tabs?: CredentialTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  children: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  // La hora local, calculada al montar — un adorno, no un reloj.
  const timeText = useMemo(() => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  }, []);

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      /*
        `max-w-2xl` y no `max-w-xl` (Anahí, 29-ago): con la cuarta
        pestaña —Direcciones— los cuatro botones se repartían 576 px y
        "Método de pago" se partía en dos renglones. Cien píxeles más y
        cada uno cabe en su línea.
      */
      className={cn("relative mx-auto mb-14 w-full max-w-2xl", className)}
    >
      {/* El resplandor bajo la card, en el violeta firma. */}
      <div className="pointer-events-none absolute inset-x-0 -bottom-10 top-[72%] rounded-[28px] bg-ds-violet-pop/90 shadow-[0_40px_80px_-16px_rgba(92,79,214,0.7)]" />
      <div className="absolute inset-x-0 -bottom-10 mx-auto w-full">
        <div className="flex items-center justify-center gap-2 py-3 text-center text-[13px] font-medium text-white">
          <Zap className="h-4 w-4" /> {t("It's The Best · Bienestar que se nota")}
        </div>
      </div>

      <div className="relative z-10 rounded-[28px] border border-ds-border bg-white shadow-2xl">
        <div className="p-6 sm:p-8">
          {/* ── Estado y hora ───────────────────────────────────────── */}
          <div className="mb-6 flex items-center justify-between text-[13px] text-ds-ink-3">
            {statusLeft ?? (
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-500" />
                <span className="select-none">{t("Cuenta activa")}</span>
              </div>
            )}
            <div className="flex items-center gap-2 opacity-80">
              <Clock className="h-4 w-4" />
              <span className="tabular-nums">{timeText}</span>
            </div>
          </div>

          {/* ── Quién soy ───────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ds-violet-pop text-[18px] font-bold text-white ring-2 ring-ds-violet-pop/20">
              {initials}
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-xl font-semibold tracking-tight text-ds-ink sm:text-2xl">
                {title}
              </h2>
              <p className="mt-0.5 text-[13px] text-ds-ink-3">{subtitle}</p>
            </div>
          </div>

          {/* ── Las pestañas: mismas píldoras, sobre blanco ─────────── */}
          {tabs && tabs.length > 1 && (
            <div
              role="tablist"
              className="mt-6 grid gap-2"
              style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => onTabChange?.(tab.key)}
                  className={cn(
                    /*
                      `whitespace-nowrap` y texto un punto menor: una
                      pestaña partida en dos renglones descuadra la fila
                      entera, y el nombre corto no admite recorte.
                    */
                    "flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl px-2 text-[13px] font-semibold transition",
                    activeTab === tab.key
                      ? "bg-ds-ink text-white"
                      : "bg-ds-mist/70 text-ds-ink-3 hover:bg-ds-mist hover:text-ds-ink",
                  )}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className="mt-6">{children}</div>
        </div>
      </div>
    </motion.div>
  );
}

/** Un dato de la credencial: etiqueta arriba, valor abajo. */
export function CredentialRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-ds-mist/50 px-4 py-2.5">
      <dt className="text-[11px] uppercase tracking-ds-label text-ds-ink-3">{label}</dt>
      <dd className="mt-0.5 truncate text-[14px] font-medium text-ds-ink">{value}</dd>
    </div>
  );
}
